import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtInt } from "@/lib/api";
import { PageHeader, Section, Pill, EmptyState } from "@/components/ui-kit";
import { Modal } from "./Books";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const empty = {
  from_type: "distribuidor", from_ref_id: "", book_id: "", quantity: 1,
  reason: "", reintegrable: true, to_warehouse_id: "",
};

export default function Returns() {
  const [items, setItems] = useState([]);
  const [books, setBooks] = useState([]);
  const [whs, setWhs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dists, setDists] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);

  async function load() {
    const [r, b, w, c, d] = await Promise.all([
      api.get("/returns"), api.get("/books"), api.get("/warehouses"),
      api.get("/customers"), api.get("/distributors"),
    ]);
    setItems(r.data); setBooks(b.data); setWhs(w.data); setCustomers(c.data); setDists(d.data);
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/returns", { ...form, quantity: parseInt(form.quantity) || 0 });
      toast.success("Devolución registrada");
      setShow(false); setForm(empty); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  const bById = Object.fromEntries(books.map(b => [b.id, b]));
  const options = form.from_type === "distribuidor" ? dists : customers;

  return (
    <div data-testid="returns-page">
      <PageHeader
        title="Devoluciones"
        subtitle="Registra unidades devueltas desde distribuidor o cliente. Si son reintegrables, vuelven al almacén propio."
        actions={
          <button onClick={() => { setForm(empty); setShow(true); }} data-testid="new-return-button"
            className="bg-brand hover:bg-brand-hover text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2">
            <Plus size={16} strokeWidth={1.5} /> Nueva devolución
          </button>
        }
      />
      <Section>
        {items.length === 0 ? <EmptyState title="Sin devoluciones" testid="returns-empty" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-[0.1em] text-ink2 bg-sidebar/40">
                <th className="text-left px-5 py-3 font-medium">Fecha</th>
                <th className="text-left px-5 py-3 font-medium">Desde</th>
                <th className="text-left px-5 py-3 font-medium">Libro</th>
                <th className="text-right px-5 py-3 font-medium">Cantidad</th>
                <th className="text-left px-5 py-3 font-medium">Motivo</th>
                <th className="text-left px-5 py-3 font-medium">Reintegrable</th>
              </tr></thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} className="border-t border-line" data-testid={`return-row-${r.id}`}>
                    <td className="px-5 py-3 text-ink2 font-mono text-xs">{fmtDate(r.date)}</td>
                    <td className="px-5 py-3"><Pill tone="muted">{r.from_type}</Pill></td>
                    <td className="px-5 py-3">{bById[r.book_id]?.title || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{fmtInt(r.quantity)}</td>
                    <td className="px-5 py-3 text-ink2 text-xs">{r.reason || "—"}</td>
                    <td className="px-5 py-3"><Pill tone={r.reintegrable ? "success" : "danger"}>{r.reintegrable ? "Sí" : "No"}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {show && (
        <Modal title="Nueva devolución" onClose={() => setShow(false)}>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="return-form">
            <F label="Tipo origen">
              <select value={form.from_type} onChange={(e) => setForm({ ...form, from_type: e.target.value, from_ref_id: "" })} className={IN}>
                <option value="distribuidor">Distribuidor</option>
                <option value="cliente">Cliente</option>
              </select>
            </F>
            <F label="Origen">
              <select value={form.from_ref_id} onChange={(e) => setForm({ ...form, from_ref_id: e.target.value })} className={IN}>
                <option value="">—</option>
                {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </F>
            <F label="Libro" required span={2}>
              <select required value={form.book_id} onChange={(e) => setForm({ ...form, book_id: e.target.value })} data-testid="return-book" className={IN}>
                <option value="">—</option>
                {books.map(b => <option key={b.id} value={b.id}>{b.title} · {b.isbn}</option>)}
              </select>
            </F>
            <F label="Cantidad" required>
              <input type="number" min="1" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="return-qty" className={IN} />
            </F>
            <F label="Reintegrable">
              <select value={form.reintegrable ? "1" : "0"} onChange={(e) => setForm({ ...form, reintegrable: e.target.value === "1" })} className={IN}>
                <option value="1">Sí, vuelve al almacén</option>
                <option value="0">No (deterioradas)</option>
              </select>
            </F>
            {form.reintegrable && (
              <F label="Almacén destino" span={2}>
                <select value={form.to_warehouse_id} onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })} className={IN}>
                  <option value="">—</option>
                  {whs.filter(w => w.type === "propio").map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </F>
            )}
            <F label="Motivo" span={2}><input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={IN} /></F>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-line rounded-sm hover:bg-sidebar">Cancelar</button>
              <button type="submit" data-testid="return-submit" className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white rounded-sm">Registrar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const IN = "w-full bg-white border border-line rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand";
function F({ label, children, required, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.12em] text-ink2">{label}{required && <span className="text-brand"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
